<script setup lang="ts">
import { computed } from 'vue';
import type { TouchView } from '@/game/arena-scene';

const props = defineProps<{
  view: TouchView;
  /**
   * Whether the gun is loaded — `reloadProgress >= 1` on a living tank, handed
   * down by `GameHud` from the same HUD payload the gunsight's ring reads, so
   * the button and the sight cannot disagree about the same reload. Deliberately
   * not part of `TouchView`: the input source owns geometry and held state, and
   * teaching it about the reload would be a second reader of trigger state.
   */
  ready: boolean;
}>();

const ring = computed(() => discStyle(props.view.layout.stick));
const fire = computed(() => discStyle(props.view.layout.fire));

/**
 * The knob is placed absolutely rather than transformed from the ring's centre.
 *
 * It rides a thumb, so it moves every frame while held; a `left`/`top` write
 * would lay out on every one of those frames. `translate` is composited, which
 * is also why the fire circle's pressed state is an opacity and a scale rather
 * than a border width.
 */
const knob = computed(() => {
  const { knobRadius } = props.view.layout.stick;
  return {
    width: `${knobRadius * 2}px`,
    height: `${knobRadius * 2}px`,
    transform: `translate3d(${props.view.knob.x - knobRadius}px, ${props.view.knob.y - knobRadius}px, 0)`,
  };
});

function discStyle(disc: { cx: number; cy: number; radius: number }) {
  return {
    width: `${disc.radius * 2}px`,
    height: `${disc.radius * 2}px`,
    transform: `translate3d(${disc.cx - disc.radius}px, ${disc.cy - disc.radius}px, 0)`,
  };
}
</script>

<template>
  <div class="touch" aria-hidden="true" data-test="touch-controls">
    <div class="ring" :class="{ held: view.stickHeld }" :style="ring" data-test="touch-stick" />
    <div class="knob" :class="{ held: view.stickHeld }" :style="knob" data-test="touch-knob" />

    <div
      class="fire"
      :class="{ held: view.fireHeld, ready }"
      :style="fire"
      data-test="touch-fire"
    >
      <svg class="shell" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.4c2.7 2 4.1 4.7 4.1 7.7v1H7.9v-1c0-3 1.4-5.7 4.1-7.7Z" fill="currentColor" />
        <rect
          x="8.6"
          y="12.7"
          width="6.8"
          height="8"
          rx="1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
        />
        <line x1="8.6" y1="18.2" x2="15.4" y2="18.2" stroke="currentColor" stroke-width="1.2" />
      </svg>
      <span class="fire-label" data-test="touch-fire-label">{{ ready ? 'READY' : 'RELOADING' }}</span>
    </div>
  </div>
</template>

<style scoped>
/*
 * Fixed to the viewport, not to the canvas, and inert.
 *
 * `pointer-events: none` is load-bearing rather than tidy: the canvas beneath is
 * where every listener lives, so anything here that swallowed a touch would eat
 * a shot or a turn — and it would do it only in the corner where the control is,
 * which is the hardest kind of input bug to recognise.
 */
.touch {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  /* The controls are drawn from the top left in CSS pixels, matching the
     coordinates `touch-layout.ts` hit-tests in. */
  contain: layout style;
}

.ring,
.knob,
.fire {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  box-sizing: border-box;
  will-change: transform;
}

/*
 * White at low alpha with a dark rim, rather than the HUD's ordinary steel.
 *
 * A control sits over the battlefield rather than over a panel, so it has to
 * read against grass, masonry, a muzzle flash and a smoke column. A single
 * flat colour fails against one of those whatever it is; a light stroke with a
 * dark outer shadow reads against all of them, which is the same reason the
 * gunsight is drawn the way it is.
 */
.ring {
  border: 3px solid rgba(255, 255, 255, 0.55);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(0, 0, 0, 0.35);
  background: rgba(10, 14, 18, 0.14);
  transition: border-color 120ms ease, background-color 120ms ease;
}

.ring.held {
  border-color: rgba(255, 255, 255, 0.8);
  background: rgba(10, 14, 18, 0.22);
}

.knob {
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
}

.knob.held {
  background: #fff;
}

/*
 * The fire circle states the reload, and that is a reversal worth recording.
 *
 * It used to hold one quiet colour on the argument that the gunsight's ring
 * already says whether the gun fired — right on desktop, where the eye lives on
 * the sight, and wrong on a phone, where the thumb lives on this button and the
 * sight is a glance away. So the button carries the two states from the
 * commissioned mockup: an amber ring, shell and READY while the gun is loaded,
 * and the whole control dimmed to steel with RELOADING while it cycles. Binary
 * rather than a progress arc, per the mock — the sight's ring is still the
 * fine-grained readout.
 *
 * The dim state is the default so a build that forgets the prop fails safe:
 * a button that never brightens is noticed; one that always claims READY lies.
 */
.fire {
  border: 3px solid rgba(183, 195, 209, 0.42);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(0, 0, 0, 0.35);
  background: rgba(10, 14, 18, 0.32);
  color: rgba(196, 204, 212, 0.75);
  display: grid;
  place-items: center;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

.fire.ready {
  border-color: rgba(232, 114, 44, 0.95);
  background: rgba(232, 114, 44, 0.16);
  color: #e8722c;
}

.fire .shell {
  width: 46%;
  height: 46%;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55));
  transition: transform 90ms ease;
}

/*
 * The label sits in the edge margin below the circle — `touch-layout.ts` keeps
 * 22px of clear ground between the disc and the safe area, and a 10px line fits
 * it at every viewport the layout clamps to. It takes the same colour as the
 * ring through `currentColor`'s parent, with a dark shadow because it reads
 * over grass and masonry, not over a panel.
 */
.fire .fire-label {
  position: absolute;
  top: calc(100% + 3px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.14em;
  color: inherit;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  white-space: nowrap;
}

/*
 * Pressed is a *scale* on the shell, not a colour swap — the ring's colour is
 * spoken for by the reload now, and the trigger is held for as long as the
 * player wants to shoot, most of which produces no shell.
 *
 * The press is on the **icon**, not on the circle: the circle's `transform`
 * carries its position and is written inline, so an inline value beats any
 * class rule here — a `transform: scale()` on `.fire.held` compiles, reads as
 * intent, and silently never applies, taking the position with it if it ever
 * did. Scaling the child is immune to that by construction.
 */
.fire.held {
  background: rgba(232, 114, 44, 0.34);
}

.fire.held .shell {
  transform: scale(0.86);
}

@media (prefers-reduced-motion: reduce) {
  .ring,
  .fire,
  .fire .shell {
    transition: none;
  }
}
</style>
